import { acceptHMRUpdate, defineStore } from "pinia";
import { markRaw, reactive, toRefs } from "vue";
import { type RouteLocationRaw } from "vue-router";

import IconGreenSettings from "@/components/icons/green/IconGreenSettings.vue";
import IconHelp from "@/components/icons/IconHelp.vue";
import IconInfo from "@/components/icons/IconInfo.vue";
import IconLogout from "@/components/icons/IconLogout.vue";
import { MatomoHelper } from "@/helpers";
import { i18n } from "@/i18n";
import type { MenuItem } from "@/types";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

import { useModalStore } from ".";

interface State {
  isMenuOpen: boolean;
  menuItems: MenuItem[];
}

export const useMenuStore = defineStore("menu", () => {
  const { t } = i18n.global;
  const { setModalState } = useModalStore();

  const trackAbcGuideClick = () => {
    MatomoHelper.sendEvent(
      MatomoEventCategory.AbcGuide,
      MatomoEventAction.ClickAbcGuide,
      "Clicked show guide"
    );
  };

  const state = reactive<State>({
    isMenuOpen: false,
    menuItems: [
      {
        id: "settings",
        type: "link",
        icon: markRaw(IconGreenSettings),
        title: t("menu.items.settings"),
        action: (): RouteLocationRaw => ({ name: "settings.general" })
      },
      {
        id: "onboarding",
        type: "button",
        icon: markRaw(IconInfo),
        title: t("menu.items.onboarding"),
        action: (): void => {
          hideMenu();
          setModalState("onboarding", true);
          trackAbcGuideClick();
        }
      },
      {
        id: "support",
        type: "button",
        icon: markRaw(IconHelp),
        title: t("menu.items.support"),
        action: (): void => {
          hideMenu();
          setModalState("support", true);
        }
      },
      {
        id: "logout",
        type: "link",
        icon: markRaw(IconLogout),
        title: t("menu.items.logout"),
        action: (): RouteLocationRaw => ({ name: "logout" })
      }
    ]
  });

  const toggleMenu = () => {
    state.isMenuOpen = !state.isMenuOpen;
  };

  const hideMenu = () => {
    state.isMenuOpen = false;
  };

  return {
    ...toRefs(state),

    toggleMenu,
    hideMenu
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useMenuStore, import.meta.hot));
}
